<template>
  <v-container fluid class="ncrm-activities">
    <div class="ncrm-activities__header" data-tour="activities-header">
      <div class="ncrm-activities__header-left">
        <v-icon class="ncrm-activities__header-icon" size="16">mdi-calendar-check-outline</v-icon>
        <h1 class="ncrm-activities__title">{{ $t("ncrm.activities.title") }}</h1>
        <span v-if="!isMobile" class="ncrm-activities__subtitle">{{ $t("ncrm.activities.subtitle") }}</span>
        <span v-else class="ncrm-activities__subtitle-mobile">{{ mobileSubtitle }}</span>
      </div>
      <div class="ncrm-activities__header-right"><template v-if="!isMobile">
          <v-btn variant="flat" color="primary" data-tour="activities-create" @click="openCreateTask">
            <v-icon start size="16">mdi-plus</v-icon>
            {{ $t("ncrm.activities.actions.addTask") }}
          </v-btn>
          <v-btn variant="text" color="primary" :loading="tableLoading" @click="loadActivities">
            <v-icon start size="16">mdi-refresh</v-icon>
            {{ $t("ncrm.activities.actions.refresh") }}
          </v-btn>
        </template><PageHelpButton />
      </div>
    </div>
    <v-card class="mt-4 ncrm-activities__panel" data-tour="activities-list">
      <v-card-text>
        <NcrmActivitiesFilters
          v-if="!isMobile"
          class="mb-4" data-tour="activities-filters"
          layout="bar"
          v-bind="{ ...filterValueProps, ...filterMetaProps }"
          @update="onFilterUpdate"
          @clear="clearFilters"
        />

        <div v-else class="ncrm-activities__mobile-toolbar mb-3">
          <v-badge
            :model-value="hasActiveFilters"
            :content="activeFilterCount"
            color="primary"
            offset-x="6"
            offset-y="6"
          >
            <v-btn
              variant="tonal"
              color="primary"
              size="small"
              prepend-icon="mdi-filter-variant"
              :aria-label="$t('ncrm.activities.actions.filters')"
              @click="openFiltersSheet"
            >
              {{ $t("ncrm.activities.actions.filters") }}
            </v-btn>
          </v-badge>
          <div class="ncrm-activities__mobile-actions">
            <v-btn
              icon="mdi-plus"
              color="tertiary"
              variant="flat"
              size="small"
              :aria-label="$t('ncrm.activities.actions.addTask')"
              @click="openCreateTask"
            />
            <v-btn
              icon="mdi-refresh"
              variant="text"
              color="primary"
              size="small"
              :loading="tableLoading"
              :aria-label="$t('ncrm.activities.actions.refresh')"
              @click="loadActivities"
            />
          </div>
        </div>

        <div v-if="isMobile" class="ncrm-activities__mobile-list">
          <v-progress-linear v-if="tableLoading" indeterminate color="primary" class="mb-2" />
          <v-alert v-if="!tableLoading && !mobileCards.length" type="info">
            {{ $t("ncrm.activities.empty") }}
          </v-alert>
          <NcrmActivityListCard
            v-for="{ item, view } in mobileCards"
            :key="`activity-card-${item.id}`"
            v-bind="view"
            @open="openSource(item)"
          />
          <v-pagination
            v-if="pageCount > 1"
            v-model="page"
            :length="pageCount"
            :total-visible="5"
            density="comfortable"
            class="mt-3"
          />
        </div>

        <v-data-table-server
          v-else
          :headers="headers"
          :items="activities"
          :items-length="totalItems"
          item-key="id"
          v-model:page="page"
          v-model:items-per-page="itemsPerPage"
          v-model:sort-by="sortBy"
          :loading="tableLoading"
          class="elevation-0 ncrm-activities__table"
          density="comfortable"
          :row-props="rowProps"
          @click:row="handleRowClick"
        >
          <template #item.title="{ item }">
            <div
              class="ncrm-activities__title-cell"
              :class="{ 'ncrm-activities__text--closed': isClosedTaskActivity(item) }"
              :title="item.content || ''"
            >
              {{ displayTitle(item) }}
            </div>
          </template>

          <template #item.activity_type="{ item }">
            <v-chip color="primary">
              {{ activityTypeLabel(item.activity_type) }}
            </v-chip>
          </template>

          <template #item.status="{ item }">
            <v-chip :color="statusColor(item.status)">
              {{ activityStatusLabel(item.status, item.activity_type) }}
            </v-chip>
          </template>

          <template #item.priority="{ item }">
            <v-chip :color="priorityColor(item.priority)" variant="outlined">
              {{ activityPriorityLabel(item.priority) }}
            </v-chip>
          </template>

          <template #item.due_at="{ item }">
            <div class="ncrm-activities__due" :class="{ 'ncrm-activities__due--overdue': isActivityOverdue(item) }">
              <div class="ncrm-activities__due-date">
                <v-icon
                  v-if="isActivityOverdue(item)"
                  size="13"
                  icon="mdi-alert-circle-outline"
                />
                <span>{{ formatDueDate(item.due_at) }}</span>
              </div>
              <div class="ncrm-activities__due-time">
                {{ formatDueTime(item.due_at) }}<template v-if="isActivityOverdue(item)"> · {{ $t("ncrm.activities.overdueBadge") }}</template>
              </div>
            </div>
          </template>

          <template #item.owner_name="{ item }">
            <div class="ncrm-activities__owner-cell">
              <span :class="{ 'ncrm-activities__text--closed': isClosedTaskActivity(item) }">
                {{ item.owner_name || (item.owner_user_id ? `#${item.owner_user_id}` : "-") }}
              </span>
              <span class="ncrm-activities__assigned-by text-caption text-medium-emphasis">
                <v-icon size="12" :icon="assignedByIcon(item)" />
                <span>{{ assignedByLabel(item) }}</span>
              </span>
            </div>
          </template>

          <template #item.entity="{ item }">
            <div class="ncrm-activities__entity-cell">
              <div
                class="ncrm-activities__entity-main text-body-2"
                :class="{ 'ncrm-activities__text--closed': isClosedTaskActivity(item) }"
              >
                <div class="ncrm-activities__entity-name">{{ item.entity_name || "-" }}</div>
                <div class="ncrm-activities__entity-context text-caption text-medium-emphasis">
                  <v-icon
                    size="13"
                    :color="activityContextColor(item)"
                    :icon="activityContextIcon(item)"
                  />
                  <span>{{ entityContextLabel(item) }}</span>
                </div>
              </div>
            </div>
          </template>

          <template #item.row_nav>
            <v-icon size="18" class="ncrm-activities__row-chevron">mdi-chevron-right</v-icon>
          </template>

          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.activities.empty") }}
            </v-alert>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>

    <v-bottom-sheet v-model="filtersSheet">
      <v-card class="ncrm-activities__filters-sheet">
        <div class="ncrm-activities__filters-sheet-header">
          <span class="ncrm-activities__filters-sheet-title">
            {{ $t("ncrm.activities.actions.filters") }}
          </span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            :aria-label="$t('app.actions.close')"
            @click="filtersSheet = false"
          />
        </div>
        <NcrmActivitiesFilters
          layout="sheet"
          v-bind="{ ...filterValueProps, ...filterMetaProps }"
          @update="onFilterUpdate"
          @clear="clearFilters"
        />
      </v-card>
    </v-bottom-sheet>

    <NcrmCreateTaskDialog
      v-model="createTaskDialogOpen"
      :owner-options="ownerOptions"
      :default-assignee-id="currentUserId"
      @created="onTaskCreated"
      @error="onTaskError"
    />

    <v-snackbar v-model="snackbar.show" :timeout="3200" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import NcrmCreateTaskDialog from "@/components/ncrm/NcrmCreateTaskDialog.vue";
import NcrmActivitiesFilters from "@/components/ncrm/NcrmActivitiesFilters.vue";
import NcrmActivityListCard from "@/components/ncrm/NcrmActivityListCard.vue";
import PageHelpButton from "@/components/tours/PageHelpButton.vue";
import {
  getNcrmActivityStatusOptions,
  getNcrmActivityTypeOptions,
  isNcrmActivityOpenStatus,
  normalizeNcrmActivityEntityType,
  normalizeNcrmActivityItem,
  normalizeNcrmActivityPriority,
  normalizeNcrmActivityStatus,
  normalizeNcrmActivityType,
  normalizeNcrmAssignmentSource,
  resolveNcrmActivityDisplayTitle,
} from "@/Utile/ncrmActivity.mjs";
import { resolveNcrmActivitySourceRoute } from "@/Utile/ncrmRouting.mjs";
import { parseCurrentUserIdFromToken } from "@/Utile/ncrmCurrentUser.mjs";
const OVERDUE_ANY = "";
const OVERDUE_TRUE = "true";
const OVERDUE_FALSE = "false";
const ENTITY_TYPE_FILTER_VALUES = Object.freeze(["contract", "deal", "onboarding", "lead", "person", "company", "user"]);
const ASSIGNMENT_SOURCE_FILTER_VALUES = Object.freeze(["user", "system", "import"]);

const normalizeAssignmentSourceFilter = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return ASSIGNMENT_SOURCE_FILTER_VALUES.includes(normalized) ? normalized : "";
};
const hasOwn = (source, key) => Object.prototype.hasOwnProperty.call(source || {}, key);

const parseBooleanQuery = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) return OVERDUE_ANY;
  if (["1", "true", "yes", "y"].includes(normalized)) return OVERDUE_TRUE;
  if (["0", "false", "no", "n"].includes(normalized)) return OVERDUE_FALSE;
  return OVERDUE_ANY;
};

const formatDateOnly = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;
  const parsed = new Date(normalized.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return "";
  const pad = (number) => String(number).padStart(2, "0");
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
};

const normalizeOptionalActivityType = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  return normalizeNcrmActivityType(raw, "") || "";
};

const normalizeEntityTypeFilter = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) return "";
  return ENTITY_TYPE_FILTER_VALUES.includes(normalized) ? normalized : "";
};

const normalizeSortOrder = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) return null;
  return normalized === "asc" || normalized === "desc" ? normalized : null;
};

const normalizeSortKey = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  const allowedKeys = ["title", "activity_type", "status", "priority", "due_at", "owner_name", "entity"];
  return allowedKeys.includes(normalized) ? normalized : null;
};

const resolveSortStateFromRoute = (query = {}) => {
  const sortKey = normalizeSortKey(query.sortBy ?? query.sort_by);
  const sortOrder = normalizeSortOrder(query.sortOrder ?? query.sort_order);
  if (!sortKey || !sortOrder) {
    return [{ key: "due_at", order: "asc" }];
  }
  return [{ key: sortKey, order: sortOrder }];
};

export default {
  name: "NcrmActivities",
  components: {
    NcrmCreateTaskDialog,
    NcrmActivitiesFilters,
    NcrmActivityListCard,
    PageHelpButton,
  },
  data() {
    return {
      createTaskDialogOpen: false,
      filtersSheet: false,
      assignmentSourceFilter: "",
      tableLoading: false,
      activities: [],
      totalItems: 0,
      page: 1,
      itemsPerPage: 25,
      sortBy: [{ key: "due_at", order: "asc" }],
      selectedType: "",
      selectedStatus: "",
      ownerUserIdFilter: null,
      ownerOptions: [],
      ownerOptionsLoading: false,
      selectedEntityType: "",
      entityNameInput: "",
      overdueFilter: OVERDUE_ANY,
      dueDateInput: "",
      currentUserId: null,
      currentUserName: "",
      currentUserEmail: "",
      routeHasExplicitOwnerFilter: false,
      routeHasNonOwnerFilters: false,
      filtersReady: false,
      filterDebounceId: null,
      snackbar: {
        show: false,
        message: "",
        color: "info",
      },
    };
  },
  computed: {
    headers() {
      return [
        { title: this.$t("ncrm.activities.headers.entity"), key: "entity" },
        { title: this.$t("ncrm.activities.headers.title"), key: "title" },
        { title: this.$t("ncrm.activities.headers.owner"), key: "owner_name" },
        { title: this.$t("ncrm.activities.headers.type"), key: "activity_type" },
        { title: this.$t("ncrm.activities.headers.status"), key: "status" },
        { title: this.$t("ncrm.activities.headers.priority"), key: "priority" },
        { title: this.$t("ncrm.activities.headers.dueAt"), key: "due_at" },
        { title: "", key: "row_nav", sortable: false, align: "end", width: 48 },
      ];
    },
    activityTypeOptions() {
      return getNcrmActivityTypeOptions().map((type) => ({
        value: type,
        label: this.activityTypeLabel(type),
      }));
    },
    statusOptions() {
      const selectedType = normalizeOptionalActivityType(this.selectedType);
      const statuses = selectedType
        ? getNcrmActivityStatusOptions(selectedType)
        : getNcrmActivityTypeOptions().flatMap((type) => getNcrmActivityStatusOptions(type));
      const unique = [...new Set(statuses)];
      return unique.map((status) => ({
        value: status,
        label: this.activityStatusLabel(status, selectedType || undefined),
      }));
    },
    entityTypeOptions() {
      return ENTITY_TYPE_FILTER_VALUES.map((value) => ({
        value,
        label: this.entityTypeLabel(value),
      }));
    },
    overdueOptions() {
      return [
        { value: OVERDUE_ANY, label: this.$t("ncrm.activities.overdue.any") },
        { value: OVERDUE_TRUE, label: this.$t("ncrm.activities.overdue.only") },
        { value: OVERDUE_FALSE, label: this.$t("ncrm.activities.overdue.exclude") },
      ];
    },
    assignmentSourceOptions() {
      return ASSIGNMENT_SOURCE_FILTER_VALUES.map((value) => ({
        value,
        label: this.assignmentSourceLabel(value),
      }));
    },
    activeFilters() {
      return {
        type: Boolean(this.selectedType),
        status: Boolean(this.selectedStatus),
        owner: Boolean(this.ownerUserIdFilter),
        entityType: Boolean(this.selectedEntityType),
        entityName: Boolean(String(this.entityNameInput || "").trim()),
        assignmentSource: Boolean(this.assignmentSourceFilter),
        overdue: this.overdueFilter === OVERDUE_TRUE || this.overdueFilter === OVERDUE_FALSE,
        dueDate: Boolean(String(this.dueDateInput || "").trim()),
      };
    },
    activeFilterCount() {
      return Object.values(this.activeFilters).filter(Boolean).length;
    },
    hasActiveFilters() {
      return this.activeFilterCount > 0;
    },
    selectedEntityTypeFilter() {
      return normalizeEntityTypeFilter(this.selectedEntityType);
    },
    backendEntityTypeFilter() {
      if (this.selectedEntityTypeFilter === "onboarding") return "deal";
      if (this.selectedEntityTypeFilter === "lead") return "";
      return normalizeNcrmActivityEntityType(this.selectedEntityTypeFilter);
    },
    isMobile() {
      return this.$vuetify.display.smAndDown;
    },
    mobileSubtitle() {
      const count = this.$t("ncrm.activities.countLabel", { count: this.totalItems });
      const owner = String(this.currentUserName || "").trim();
      return owner ? `${count} · ${owner}` : count;
    },
    pageCount() {
      const perPage = Number(this.itemsPerPage) || 1;
      if (perPage <= 0) return 1;
      return Math.max(1, Math.ceil((Number(this.totalItems) || 0) / perPage));
    },
    mobileCards() {
      return (Array.isArray(this.activities) ? this.activities : []).map((item) => ({
        item,
        view: this.buildRowView(item),
      }));
    },
    filterValueProps() {
      return {
        selectedEntityType: this.selectedEntityType,
        ownerUserIdFilter: this.ownerUserIdFilter,
        selectedType: this.selectedType,
        selectedStatus: this.selectedStatus,
        assignmentSourceFilter: this.assignmentSourceFilter,
        entityNameInput: this.entityNameInput,
        overdueFilter: this.overdueFilter,
        dueDateInput: this.dueDateInput,
      };
    },
    filterMetaProps() {
      return {
        entityTypeOptions: this.entityTypeOptions,
        ownerOptions: this.ownerOptions,
        ownerOptionsLoading: this.ownerOptionsLoading,
        activityTypeOptions: this.activityTypeOptions,
        statusOptions: this.statusOptions,
        assignmentSourceOptions: this.assignmentSourceOptions,
        overdueOptions: this.overdueOptions,
        activeFilters: this.activeFilters,
        activeFilterCount: this.activeFilterCount,
        hasActiveFilters: this.hasActiveFilters,
        count: this.totalItems,
      };
    },
  },
  watch: {
    page() {
      this.loadActivities();
    },
    itemsPerPage() {
      if (this.page !== 1) {
        this.page = 1;
        return;
      }
      this.loadActivities();
    },
    sortBy: {
      deep: true,
      handler() {
        this.queueFilterReload({ immediate: true });
      },
    },
    selectedType(nextType) {
      const normalizedType = normalizeOptionalActivityType(nextType);
      if (!this.selectedStatus) {
        this.queueFilterReload({ immediate: true });
        return;
      }
      const normalizedStatus = normalizeNcrmActivityStatus(this.selectedStatus, normalizedType || undefined, false);
      if (!normalizedStatus) {
        this.selectedStatus = "";
        return;
      }
      this.queueFilterReload({ immediate: true });
    },
    selectedStatus() {
      this.queueFilterReload({ immediate: true });
    },
    ownerUserIdFilter() {
      this.queueFilterReload({ immediate: true });
    },
    selectedEntityType() {
      this.queueFilterReload({ immediate: true });
    },
    assignmentSourceFilter() {
      this.queueFilterReload({ immediate: true });
    },
    entityNameInput() {
      this.queueFilterReload();
    },
    overdueFilter() {
      this.queueFilterReload({ immediate: true });
    },
    dueDateInput() {
      this.queueFilterReload();
    },
  },
  async mounted() {
    this.filtersReady = false;
    this.readFiltersFromRoute();
    await this.resolveCurrentUser();
    await this.fetchOwnerOptions();
    this.applyDefaultOwnerFilter();
    await this.loadActivities();
    this.filtersReady = true;
  },
  beforeUnmount() {
    this.clearFilterDebounce();
  },
  methods: {
    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        message: String(message || ""),
        color,
      };
    },
    formatDueDate(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "-";
      const parsed = new Date(normalized.replace(" ", "T"));
      if (Number.isNaN(parsed.getTime())) return normalized;
      const pad = (number) => String(number).padStart(2, "0");
      return `${pad(parsed.getDate())}.${pad(parsed.getMonth() + 1)}.${parsed.getFullYear()}`;
    },
    formatDueTime(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "";
      const parsed = new Date(normalized.replace(" ", "T"));
      if (Number.isNaN(parsed.getTime())) return "";
      const pad = (number) => String(number).padStart(2, "0");
      return `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
    },
    displayTitle(item) {
      return resolveNcrmActivityDisplayTitle(item, { maxLength: 80, fallback: "-" });
    },
    activityTypeLabel(type) {
      const normalized = normalizeNcrmActivityType(type, "note") || "note";
      const key = `ncrm.activity.types.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    activityStatusLabel(status, type) {
      const normalized = normalizeNcrmActivityStatus(status, type, false) || String(status || "");
      const key = `ncrm.activity.statuses.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    activityPriorityLabel(priority) {
      const normalized = normalizeNcrmActivityPriority(priority, "normal") || "normal";
      const key = `ncrm.activity.priorities.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    entityTypeLabel(type) {
      const normalized = normalizeEntityTypeFilter(type) || normalizeNcrmActivityEntityType(type) || "company";
      const key = `ncrm.activities.entityTypes.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    resolveActivityContextKey(item = {}) {
      const entityType = normalizeNcrmActivityEntityType(item?.entity_type ?? item?.entityType);
      if (this.parsePositiveInt(item?.onboarding_id ?? item?.onboardingId)) return "onboarding";
      if (entityType === "deal" || entityType === "contract") return entityType;
      const originType = String(item?.origin_entity_type ?? item?.originEntityType ?? "").trim().toLowerCase();
      if (originType === "lead_entry") return "lead";
      return entityType || "company";
    },
    entityContextLabel(item = {}) {
      const key = this.resolveActivityContextKey(item);
      const translated = this.$t(`ncrm.activities.entityTypes.${key}`);
      return translated === `ncrm.activities.entityTypes.${key}` ? key : translated;
    },
    activityContextIcon(item = {}) {
      const key = this.resolveActivityContextKey(item);
      const icons = {
        company: "mdi-domain",
        person: "mdi-account-outline",
        contract: "mdi-file-document-outline",
        lead: "mdi-flag-outline",
        deal: "mdi-briefcase-outline",
        onboarding: "mdi-rocket-launch-outline",
        user: "mdi-account-tie-outline",
      };
      return icons[key] || "mdi-shape-outline";
    },
    activityContextColor(item = {}) {
      const key = this.resolveActivityContextKey(item);
      const colors = {
        company: "quaternary",
        person: "warning",
        contract: "quaternary",
        lead: "info",
        deal: "primary",
        onboarding: "success",
        user: "info",
      };
      return colors[key] || "primary";
    },
    rowAccentClass(item = {}) {
      if (this.isActivityOverdue(item)) return "ncrm-activities__row ncrm-activities__row--overdue";
      return `ncrm-activities__row ncrm-activities__row--${this.resolveActivityContextKey(item)}`;
    },
    rowProps(data = {}) {
      return { class: this.rowAccentClass(data?.item || {}) };
    },
    statusColor(status) {
      const value = String(status || "").toLowerCase();
      if (["open", "planned", "in_progress"].includes(value)) return "warning";
      if (["done", "sent", "replied", "held", "logged"].includes(value)) return "success";
      if (["failed", "canceled", "no_answer", "no_show"].includes(value)) return "error";
      return "primary";
    },
    priorityColor(priority) {
      const value = String(priority || "").toLowerCase();
      if (value === "high") return "error";
      if (value === "low") return "default";
      return "primary";
    },
    parsePositiveInt(value) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return null;
      const normalized = Math.trunc(parsed);
      return normalized > 0 ? normalized : null;
    },
    parseDateToMs(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return null;
      const parsed = new Date(normalized.replace(" ", "T"));
      if (Number.isNaN(parsed.getTime())) return null;
      return parsed.getTime();
    },
    buildDueDateRange() {
      const normalized = String(this.dueDateInput || "").trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
      return {
        dueFrom: `${normalized} 00:00:00`,
        dueTo: `${normalized} 23:59:59`,
      };
    },
    isActivityOverdue(activity) {
      const dueAtMs = this.parseDateToMs(activity?.due_at);
      if (dueAtMs === null) return false;
      if (dueAtMs >= Date.now()) return false;
      return isNcrmActivityOpenStatus(activity?.activity_type, activity?.status);
    },
    isClosedTaskActivity(activity) {
      const type = normalizeNcrmActivityType(activity?.activity_type, null);
      if (!type || type === "note") return false;
      return !isNcrmActivityOpenStatus(type, activity?.status);
    },
    async resolveCurrentUser() {
      this.currentUserId = parseCurrentUserIdFromToken();
      try {
        const { data } = await axios.get("/me");
        const resolvedId = this.parsePositiveInt(data?.id);
        if (resolvedId) this.currentUserId = resolvedId;
        this.currentUserName = String(data?.name || "").trim();
        this.currentUserEmail = String(data?.email || "").trim();
      } catch (_) {
        // fallback silent: token payload is already used above
      }
    },
    normalizeOwnerOption(option = {}) {
      const id = this.parsePositiveInt(option?.id);
      if (!id) return null;
      const name = String(option?.name || "").trim();
      const email = String(option?.email || "").trim();
      const displayName = String(option?.display_name || option?.displayName || "").trim();
      const label = displayName || name || email || `#${id}`;
      return {
        id,
        label,
        name,
        email,
      };
    },
    ensureCurrentOwnerOption() {
      const currentId = this.parsePositiveInt(this.currentUserId);
      if (!currentId) return;
      const alreadyListed = (this.ownerOptions || []).some((item) => this.parsePositiveInt(item?.id) === currentId);
      if (alreadyListed) return;
      const label = String(this.currentUserName || this.currentUserEmail || "").trim() || `#${currentId}`;
      this.ownerOptions = [{ id: currentId, label, name: this.currentUserName, email: this.currentUserEmail }, ...this.ownerOptions];
    },
    async fetchOwnerOptions() {
      this.ownerOptionsLoading = true;
      try {
        const { data } = await axios.get("/ncrm/activity/owners", {
          params: {
            limit: 200,
          },
        });
        const options = Array.isArray(data?.items) ? data.items : [];
        this.ownerOptions = options
          .map((item) => this.normalizeOwnerOption(item))
          .filter(Boolean);
      } catch (_) {
        this.ownerOptions = [];
      } finally {
        this.ensureCurrentOwnerOption();
        this.ownerOptionsLoading = false;
      }
    },
    applyDefaultOwnerFilter({ force = false } = {}) {
      if (!force && (this.routeHasExplicitOwnerFilter || this.routeHasNonOwnerFilters)) return;
      if (this.ownerUserIdFilter) return;
      if (!this.currentUserId) return;
      this.ownerUserIdFilter = this.currentUserId;
    },
    clearFilterDebounce() {
      if (!this.filterDebounceId) return;
      clearTimeout(this.filterDebounceId);
      this.filterDebounceId = null;
    },
    queueFilterReload({ immediate = false } = {}) {
      if (!this.filtersReady) return;
      this.clearFilterDebounce();

      if (this.page !== 1) {
        this.page = 1;
        return;
      }

      if (immediate) {
        this.loadActivities();
        return;
      }

      this.filterDebounceId = setTimeout(() => {
        this.filterDebounceId = null;
        this.loadActivities();
      }, 300);
    },
    readFiltersFromRoute() {
      const query = this.$route?.query || {};
      this.routeHasExplicitOwnerFilter = hasOwn(query, "ownerUserId") || hasOwn(query, "owner_user_id");
      this.routeHasNonOwnerFilters =
        hasOwn(query, "activityType") ||
        hasOwn(query, "activity_type") ||
        hasOwn(query, "status") ||
        hasOwn(query, "entityType") ||
        hasOwn(query, "entity_type") ||
        hasOwn(query, "entityName") ||
        hasOwn(query, "entity_name") ||
        hasOwn(query, "assignmentSource") ||
        hasOwn(query, "assignment_source") ||
        hasOwn(query, "overdue") ||
        hasOwn(query, "dueDate") ||
        hasOwn(query, "due_date") ||
        hasOwn(query, "dueFrom") ||
        hasOwn(query, "due_from") ||
        hasOwn(query, "dueTo") ||
        hasOwn(query, "due_to");
      this.selectedType = normalizeOptionalActivityType(query.activityType ?? query.activity_type);
      this.selectedEntityType = normalizeEntityTypeFilter(query.entityType ?? query.entity_type);
      this.selectedStatus =
        normalizeNcrmActivityStatus(query.status, this.selectedType || undefined, false) || "";
      this.ownerUserIdFilter = this.parsePositiveInt(query.ownerUserId ?? query.owner_user_id);
      this.entityNameInput = String(query.entityName ?? query.entity_name ?? "").trim();
      this.assignmentSourceFilter = normalizeAssignmentSourceFilter(query.assignmentSource ?? query.assignment_source);
      this.overdueFilter = parseBooleanQuery(query.overdue);
      const routeDueDate = query.dueDate ?? query.due_date;
      if (String(routeDueDate ?? "").trim()) {
        this.dueDateInput = formatDateOnly(routeDueDate);
      } else {
        this.dueDateInput = formatDateOnly(query.dueFrom ?? query.due_from ?? query.dueTo ?? query.due_to);
      }
      const routePage = Number(query.page);
      const routeLimit = Number(query.limit);
      this.page = Number.isFinite(routePage) && routePage > 0 ? Math.trunc(routePage) : 1;
      this.itemsPerPage = Number.isFinite(routeLimit) && routeLimit > 0 ? Math.trunc(routeLimit) : 25;
      this.sortBy = resolveSortStateFromRoute(query);
    },
    buildQueryParams() {
      const params = {
        page: this.page,
        limit: this.itemsPerPage,
      };

      const selectedType = normalizeOptionalActivityType(this.selectedType);
      if (selectedType) params.activityType = selectedType;
      if (!this.selectedType) params.excludeNotes = true;
      if (this.selectedStatus) params.status = this.selectedStatus;
      if (!this.selectedStatus && selectedType !== "note") params.openOnly = true;
      const activeSort = Array.isArray(this.sortBy) && this.sortBy.length ? this.sortBy[0] : null;
      const sortKey = normalizeSortKey(activeSort?.key);
      const sortOrder = normalizeSortOrder(activeSort?.order);
      if (sortKey && sortOrder) {
        params.sortBy = sortKey;
        params.sortOrder = sortOrder;
      }
      if (this.ownerUserIdFilter) params.ownerUserId = this.ownerUserIdFilter;
      if (this.backendEntityTypeFilter) params.entityType = this.backendEntityTypeFilter;
      if (
        this.selectedEntityTypeFilter === "deal" ||
        this.selectedEntityTypeFilter === "onboarding" ||
        this.selectedEntityTypeFilter === "lead"
      ) {
        params.entityContext = this.selectedEntityTypeFilter;
      }
      if (this.entityNameInput) params.entityName = this.entityNameInput;
      if (this.assignmentSourceFilter) params.assignmentSource = this.assignmentSourceFilter;
      if (this.overdueFilter === OVERDUE_TRUE) params.overdue = true;
      if (this.overdueFilter === OVERDUE_FALSE) params.overdue = false;
      const dueRange = this.buildDueDateRange();
      if (dueRange) {
        params.dueFrom = dueRange.dueFrom;
        params.dueTo = dueRange.dueTo;
      }

      return params;
    },
    syncRouteQuery() {
      const query = {};
      if (this.selectedType) query.activityType = this.selectedType;
      if (this.selectedStatus) query.status = this.selectedStatus;
      if (this.ownerUserIdFilter) query.ownerUserId = String(this.ownerUserIdFilter);
      if (this.selectedEntityTypeFilter) query.entityType = this.selectedEntityTypeFilter;
      if (this.entityNameInput) query.entityName = this.entityNameInput;
      if (this.assignmentSourceFilter) query.assignmentSource = this.assignmentSourceFilter;
      if (this.overdueFilter === OVERDUE_TRUE || this.overdueFilter === OVERDUE_FALSE) {
        query.overdue = this.overdueFilter;
      }
      if (this.dueDateInput) query.dueDate = this.dueDateInput;
      const activeSort = Array.isArray(this.sortBy) && this.sortBy.length ? this.sortBy[0] : null;
      const sortKey = normalizeSortKey(activeSort?.key);
      const sortOrder = normalizeSortOrder(activeSort?.order);
      if (sortKey && sortOrder) {
        query.sortBy = sortKey;
        query.sortOrder = sortOrder;
      }
      query.page = String(this.page);
      query.limit = String(this.itemsPerPage);

      this.$router.replace({ query }).catch(() => {});
    },
    async loadActivities() {
      this.tableLoading = true;
      try {
        const { data } = await axios.get("/ncrm/activity", {
          params: this.buildQueryParams(),
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        this.activities = items.map((item) => normalizeNcrmActivityItem(item));
        this.totalItems = Number(data?.total || 0);
        this.syncRouteQuery();
      } catch (error) {
        this.activities = [];
        this.totalItems = 0;
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.activities.messages.loadError"),
          "error"
        );
      } finally {
        this.tableLoading = false;
      }
    },
    clearFilters() {
      const shouldAutoReload = this.filtersReady;
      this.filtersReady = false;
      this.selectedType = "";
      this.selectedStatus = "";
      this.ownerUserIdFilter = null;
      this.selectedEntityType = "";
      this.entityNameInput = "";
      this.assignmentSourceFilter = "";
      this.overdueFilter = OVERDUE_ANY;
      this.dueDateInput = "";
      this.applyDefaultOwnerFilter({ force: true });
      this.page = 1;
      this.filtersReady = shouldAutoReload;
      if (shouldAutoReload) {
        this.queueFilterReload({ immediate: true });
      }
    },
    openSource(item) {
      const routeLocation = resolveNcrmActivitySourceRoute(item);
      if (!routeLocation) {
        this.showSnackbar(this.$t("ncrm.activities.messages.sourceRouteUnavailable"), "warning");
        return;
      }
      this.$router.push(routeLocation);
    },
    handleRowClick(_, row) {
      this.openSource(row?.item || null);
    },
    assignmentSourceLabel(value) {
      const normalized = normalizeNcrmAssignmentSource(value);
      const key = `ncrm.activities.assignmentSources.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    assignedByIcon(item = {}) {
      return normalizeNcrmAssignmentSource(item?.assignment_source) === "system"
        ? "mdi-robot-outline"
        : "mdi-account-arrow-right-outline";
    },
    assignedByLabel(item = {}) {
      const source = normalizeNcrmAssignmentSource(item?.assignment_source);
      if (source === "system") {
        return this.$t("ncrm.activities.assignedBySystem");
      }
      const name = String(item?.assigned_by_name || "").trim()
        || (item?.assigned_by_user_id ? `#${item.assigned_by_user_id}` : "");
      if (!name) {
        return this.assignmentSourceLabel(source);
      }
      return this.$t("ncrm.activities.assignedByUser", { name });
    },
    shortDueText(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "-";
      const parsed = new Date(normalized.replace(" ", "T"));
      if (Number.isNaN(parsed.getTime())) return normalized;
      const pad = (number) => String(number).padStart(2, "0");
      return `${pad(parsed.getDate())}.${pad(parsed.getMonth() + 1)}, ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
    },
    buildRowView(item = {}) {
      const overdue = this.isActivityOverdue(item);
      return {
        entityName: item.entity_name || "-",
        title: this.displayTitle(item),
        contextLabel: this.entityContextLabel(item),
        contextIcon: this.activityContextIcon(item),
        contextColor: this.activityContextColor(item),
        typeLabel: this.activityTypeLabel(item.activity_type),
        statusLabel: this.activityStatusLabel(item.status, item.activity_type),
        statusColor: this.statusColor(item.status),
        dueText: this.shortDueText(item.due_at),
        overdue,
        overdueLabel: this.$t("ncrm.activities.overdueBadge"),
        accentColorKey: overdue ? "error" : this.activityContextColor(item),
        isClosed: this.isClosedTaskActivity(item),
      };
    },
    openFiltersSheet() {
      this.filtersSheet = true;
    },
    onFilterUpdate({ field, value } = {}) {
      if (!field || !Object.prototype.hasOwnProperty.call(this.filterValueProps, field)) return;
      this[field] = value;
    },
    openCreateTask() {
      this.createTaskDialogOpen = true;
    },
    async onTaskCreated() {
      this.showSnackbar(this.$t("ncrm.activities.createTask.messages.created"), "success");
      await this.loadActivities();
    },
    onTaskError(error) {
      this.showSnackbar(
        error?.response?.data?.error || this.$t("ncrm.activities.createTask.messages.createError"),
        "error"
      );
    },
  },
};
</script>

<style scoped src="./NcrmActivities.css"></style>
